/* =============================================================================
   yojo-shop.js — ONE controller for both selector-shop sections:
     sections/yojo-home-category-shop.liquid   (6 category selectors, multi-row panels)
     sections/yojo-home-vendor-shop.liquid     (3 vendor selectors, single-rail panels)

   reference-parity band: whole-card paging, exact resting positions, infinite loop
   via inert edge clones, RTL-correct, one instance per Shopify section.

   Contract with the Liquid:
     [data-yojo-shop]                     section root; carries data-shop-mode + data-shop-uid
     [data-shop-track]                    the horizontal selector track (scroll-snap)
     [data-shop-sel]                      a selector — ALWAYS an <a href> (real fallback link)
                                          carries data-panel="<panel id>"
     [data-shop-arrow][data-dir=start|end] selector arrows
     [data-shop-panel="<id>"]             a live panel
     template[data-shop-tpl="<id>"]       an inactive panel, inert until first activation
     [data-shop-panels]                   the container templates are hydrated into
     [data-rail]                          a product rail (horizontal scroll-snap)
     [data-rail-arrow][data-dir]          rail arrows, scoped by [data-rail-group]
     [data-shop-live]                     visually-hidden aria-live=polite announcer

   Design notes
     - Selectors stay <a href> forever. JS only intercepts unmodified primary clicks, so
       middle-click / Cmd-click / no-JS / crawlers all keep real navigation to the collection.
     - NOT a tablist. Links + aria-current="true" + aria-controls, panels are labelled regions.
       (A tablist would require abandoning link semantics; a half-tablist is worse than neither.)
     - Every instance is scoped to its own section root. No document-level selectors.
     - All listeners are attached through one AbortController per instance, so
       shopify:section:unload removes every one of them with a single abort().
   ========================================================================== */
(function () {
  'use strict';

  /* Every section using this component emits its own <script src> tag, and the browser
     EXECUTES the file once per tag. Without this latch the IIFE ran 6x on the homepage:
     6 independent WeakMaps, so every root looked uninitialised 6 times -> 6x edge clones
     (36 instead of 6), 6 sets of listeners, 6 re-seats fighting each other (the band
     appeared frozen) and 6 analytics pushes per interaction. */
  if (window.__yojoShopLoaded) return;
  window.__yojoShopLoaded = 1;

  var REDUCE = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');
  var behavior = function () { return REDUCE && REDUCE.matches ? 'auto' : 'smooth'; };
  var instances = new WeakMap();

  /* ---------------------------------------------------------------- helpers */

  /* the reference parity: the band moves by WHOLE cards and always settles on an exact
     card boundary. pitch = card width + gap, measured live so it survives resize. */
  function pitchOf(track) {
    var a = track.querySelector('[data-shop-sel], [data-rail-item]');
    if (!a) return track.clientWidth;
    var st = getComputedStyle(track);
    var gap = parseFloat(st.columnGap || st.gap || '0') || 0;
    return a.getBoundingClientRect().width + gap;
  }

  /* whole cards visible — 3 desktop, 1 mobile. No magic numbers. */
  function perView(track) {
    var pitch = pitchOf(track);
    if (!pitch) return 1;
    return Math.max(1, Math.floor((track.clientWidth + 2) / pitch));
  }

  /* RTL-safe: scrollLeft runs 0 -> negative in RTL. Work in a normalised,
     non-negative "distance travelled from the start of the reading order". */
  function isRtl(el) { return getComputedStyle(el).direction === 'rtl'; }
  function scrollPos(t) { return Math.abs(t.scrollLeft); }
  function maxScroll(t) { return Math.max(0, t.scrollWidth - t.clientWidth); }

  function setPos(track, pos, instant) {
    var clamped = Math.max(0, Math.min(pos, maxScroll(track)));
    var left = isRtl(track) ? -clamped : clamped;
    if (instant) {
      var prev = track.style.scrollBehavior;
      track.style.scrollBehavior = 'auto';     /* no transition => no visible jump */
      track.scrollLeft = left;
      /* force layout so the browser applies it before behaviour is restored */
      void track.offsetWidth;
      track.style.scrollBehavior = prev || '';
    } else {
      track.scrollTo({ left: left, behavior: behavior() });
    }
  }

  /* ------------------------------------------------------------ infinite loop
     Clone `perView` items onto each end so the band is continuous. When a settle
     lands inside a clone zone we re-seat by exactly one real-set width with the
     transition suppressed — same pixels on screen, no flash, no DOM rebuild.
     Clones are inert: no id, aria-hidden, tabindex -1, never in inst.selectors,
     never counted by analytics or the dots.                                     */
  function buildLoop(inst) {
    var track = inst.track;
    if (!track || inst.loop || inst.building) return;
    inst.building = 1;
    var reals = inst.selectors;
    var pv = perView(track);
    if (reals.length <= pv) { inst.building = 0; return; }   /* nothing to loop */

    var mk = function (src) {
      var c = src.cloneNode(true);
      c.removeAttribute('id');
      c.removeAttribute('aria-current');
      c.setAttribute('aria-hidden', 'true');
      c.setAttribute('tabindex', '-1');
      c.setAttribute('data-shop-clone', '');
      c.removeAttribute('data-shop-sel');      /* excluded from selector state */
      /* 2026-09-02 — "vendor banners don't load". cloneNode copies the
         fade-in STATE yojo-motion.js stamped on the source image
         (.yojo-img-fade-in + data-yojo-fade-bound) but not its load listener,
         so a clone whose bytes had not arrived at clone time stayed at
         opacity:0 forever — measured live: every hidden hero on the homepage
         (12 desktop / 8 mobile) was an edge clone, zero real tiles. Reset the
         state: an already-decoded image is simply marked loaded; a pending one
         is handed back to the motion module, which re-binds it after the
         clones are in the DOM (refresh below). */
      c.querySelectorAll('img').forEach(function (img) {
        if (img.complete && img.naturalWidth > 0) {
          img.classList.add('is-loaded');
        } else {
          img.removeAttribute('data-yojo-fade-bound');
          img.classList.remove('yojo-img-fade-in', 'is-loaded');
        }
      });
      return c;
    };
    var head = document.createDocumentFragment(), tail = document.createDocumentFragment();
    for (var i = 0; i < pv; i++) {
      head.appendChild(mk(reals[reals.length - pv + i]));
      tail.appendChild(mk(reals[i]));
    }
    track.insertBefore(head, reals[0]);
    track.appendChild(tail);
    if (window.YojoMotion && typeof window.YojoMotion.refresh === 'function') {
      window.YojoMotion.refresh(track);
    }

    inst.loop = { pv: pv, count: reals.length };
    setPos(track, pv * pitchOf(track), true);  /* start on the first REAL card */
    inst.building = 0;
  }

  /* re-seat out of a clone zone once the band has settled.
     Never while a finger is down (2026-11-02, live Pixel 7 trace): this runs
     from the 140ms scroll debounce, which cannot tell "resting" from "held
     still". Under `scroll-snap-type: x mandatory` the programmatic re-seat is
     itself snapped, so the tile under the finger jumped 123-127px in one
     frame. inst.touching (init()) defers it to release; the settle that
     follows the release then re-seats with a 0px visual step. */
  function normalise(inst) {
    if (!inst.loop || inst.settling || inst.touching) return;
    var track = inst.track, pitch = pitchOf(track);
    if (!pitch) return;
    var setW = inst.loop.count * pitch;
    var pos = scrollPos(track);
    var lead = inst.loop.pv * pitch;
    if (pos < lead - pitch * 0.5) { inst.settling = 1; setPos(track, pos + setW, true); inst.settling = 0; }
    else if (pos > lead + setW - pitch * 0.5) { inst.settling = 1; setPos(track, pos - setW, true); inst.settling = 0; }
  }

  /* ------------------------------------------------------ SETTLED TILE
     Rail-snap (2026-08-22, owner-reported "swipe moves the tile but not the
     products"). THE one nearest-tile computation. Every consumer — panel
     selection on settle, aria-current, the dots, the clone highlight — reads
     this, so they can never disagree.

     The track is start-aligned (scroll-snap-align: start, mandatory) and each
     tile is exactly one pitch, so the settled tile is the on-grid index
     Math.round(pos / pitch). With the infinite loop active the first `pv`
     positions are head clones, so the real index is (grid - pv) mod count —
     which is also how a clone maps back to its canonical vendor. Returns -1
     when nothing is measurable (no track, no pitch, hidden section). */
  function settledIndex(inst) {
    var track = inst.track;
    if (!track || !inst.selectors.length) return -1;
    var pitch = pitchOf(track);
    if (!pitch) return -1;
    var grid = Math.round(scrollPos(track) / pitch);
    var count = inst.selectors.length;
    if (inst.loop) grid = ((grid - inst.loop.pv) % count + count) % count;
    return Math.max(0, Math.min(count - 1, grid));
  }

  /* The on-grid track position that puts real selector `idx` at the snap
     start. Inverse of settledIndex(), so seatAt(i) always settles on i. */
  function seatAt(inst, idx, instant) {
    var track = inst.track;
    if (!track) return;
    var pitch = pitchOf(track);
    if (!pitch) return;
    var target = (inst.loop ? inst.loop.pv + idx : idx) * pitch;
    setPos(track, target, instant);
  }

  /* Selection follows the settled tile after ANY scroll that came to rest —
     finger, wheel, arrow or a programmatic seat. A partial swipe that
     settles back on the current tile is a no-op (same index, same selector),
     so no spurious panel swap and no scroll/select recursion: select() on
     settle never scrolls (scrollIntoView:false).

     2026-09-26 (audit website-az-2026-09-25, runner home-a11y-speed #2):
     only when a PERSON moved the band — a finger or pen on the track, a wheel
     or trackpad swipe over it, or a band arrow (inst.moved, set in init()).
     Tabbing onto an off-screen tile makes the browser scroll the track to
     reveal it, and settle() then selected whichever tile the track landed
     on: focus alone swapped the products (WCAG 3.2.1), and a settle while
     focus sat in the active panel hid the focused control (focus fell to
     <body>, 2.4.3). Focus moving into the track or the active panel clears
     inst.moved, so keyboard and screen-reader focus never chooses; Enter on
     a tile does (its click handler). Boot seats and resize re-seats never
     choose either. */
  function settle(inst) {
    normalise(inst);
    syncArrows(inst);
    var idx = settledIndex(inst);
    /* A held finger has not chosen yet: the panel follows the tile only after
       release (release re-arms the settle). */
    if (idx >= 0 && maxScroll(inst.track) > 1 && inst.moved && !inst.touching) {
      var sel = inst.selectors[idx];
      if (sel && sel !== inst.active) select(inst, sel, { scrollIntoView: false, source: 'scroll' });
    }
    syncDots(inst);
  }

  /* ------------------------------------------------- PHYSICAL DIRECTION CONTRACT
     dir is 'right' or 'left' — what the customer SEES, never array order.

       'right' -> reveal content hidden on the physical RIGHT
       'left'  -> reveal content hidden on the physical LEFT

     In RTL the reading order starts on the right, so travelling physically right
     means DECREASING the normalised scroll distance; in LTR it means increasing
     it. One translation, in one place. No component may invent its own.        */
  function physicalToDelta(track, dir) {
    var rtl = isRtl(track);
    if (dir === 'right') return rtl ? -1 : 1;
    if (dir === 'left') return rtl ? 1 : -1;
    return 0;
  }

  /* move exactly N whole cards; with a loop active there are no bounds */
  function page(track, dir, inst) {
    var pitch = pitchOf(track);
    if (!pitch) return;
    var n = perView(track);
    var current = Math.round(scrollPos(track) / pitch);
    var target = (current + physicalToDelta(track, dir) * n) * pitch;
    if (inst && inst.loop) {
      /* pre-seat so a backwards move from the head still has band to travel into */
      if (target < 0) { setPos(track, scrollPos(track) + inst.loop.count * pitch, true); target += inst.loop.count * pitch; }
      setPos(track, target, false);
    } else {
      setPos(track, target, false);
    }
  }

  function syncArrows(inst) {
    var track = inst.track;
    if (!track || !inst.arrows.length) return;
    var overflow = maxScroll(track) > 1;
    inst.arrows.forEach(function (b) {
      b.hidden = !overflow;                     /* hide when everything fits */
      if (!overflow) return;
      if (inst.loop) { b.disabled = false; return; }   /* looping never dead-ends */
      var pos = scrollPos(track);
      /* which physical arrow is dead depends on the writing direction */
      var atStart = pos <= 1, atEnd = pos >= maxScroll(track) - 1;
      var rtl = isRtl(track);
      if (b.dataset.dir === 'right') b.disabled = rtl ? atStart : atEnd;
      else b.disabled = rtl ? atEnd : atStart;
    });
  }

  /* mirror the active look onto whichever clone is currently on screen */
  function syncClones(inst) {
    if (!inst.loop) return;
    var active = inst.active ? inst.active.dataset.panel : null;
    inst.root.querySelectorAll('[data-shop-clone]').forEach(function (c) {
      c.classList.toggle('is-on', !!active && c.dataset.panel === active);
    });
  }

  /* ------------------------------------------------------------- analytics */

  function track_(inst, event, data) {
    /* Guard 1: never emit from the theme editor — Shopify re-renders sections there and
       would otherwise double-count every interaction. */
    if (window.Shopify && window.Shopify.designMode) return;
    /* Guard 2: impression-type events fire at most once per instance. */
    if (data && data.once) {
      if (inst.fired.has(event + '|' + (data.id || ''))) return;
      inst.fired.add(event + '|' + (data.id || ''));
      delete data.once;
    }
    try {
      window.dataLayer = window.dataLayer || [];
      var p = { event: event, section_id: inst.uid, component: inst.mode };
      for (var k in data) p[k] = data[k];
      window.dataLayer.push(p);
    } catch (e) { /* analytics must never break the UI */ }
  }

  /* ------------------------------------------------------ panel activation */

  /* Hydrate an inactive panel exactly once, from its local <template>.
     No network request — the markup was server-rendered by Liquid into the template. */
  function hydrate(inst, id) {
    if (inst.hydrated.has(id)) return inst.root.querySelector('[data-shop-panel="' + CSS.escape(id) + '"]');
    var tpl = inst.root.querySelector('template[data-shop-tpl="' + CSS.escape(id) + '"]');
    if (!tpl) return inst.root.querySelector('[data-shop-panel="' + CSS.escape(id) + '"]');

    var frag = tpl.content.cloneNode(true);
    var panel = frag.querySelector('[data-shop-panel]');
    (inst.panelHost || tpl.parentNode).insertBefore(frag, tpl);
    inst.hydrated.add(id);
    tpl.remove();

    /* G2 (axe aria-valid-attr-value, 2026-08-01): the panel now exists in the DOM, so it's
       safe for its selector anchor(s) to reference it. Server-render deliberately omits
       aria-controls for non-default anchors (their panel was still an inert <template>). */
    var owningSel = inst.root.querySelector('[data-shop-sel][data-panel="' + CSS.escape(id) + '"]');
    if (owningSel) owningSel.setAttribute('aria-controls', id);

    /* Canonical product-card behaviour re-initialisation.
       The KROWN cards are custom elements (<product-form>, <quick-add-to-cart>), which upgrade
       automatically on insertion into the document — nothing to do for those. Anything in the
       theme that binds imperatively listens for this event instead of DOMContentLoaded. */
    if (panel) {
      /* The canonical card's behaviour is delivered by CUSTOM ELEMENTS
         (<quick-add-to-cart>, <product-form>), which upgrade themselves the moment
         they are connected to the document — so hydration needs no re-init call.
         We emit one namespaced event for any theme code that binds imperatively.
         We deliberately do NOT re-dispatch a synthetic 'shopify:section:load':
         the theme's global handlers expect a real section container and threw
         "Cannot read properties of undefined (reading 'contains')" when fed ours. */
      panel.dispatchEvent(new CustomEvent('yojo:cards:added', { bubbles: true, detail: { container: panel } }));
    }
    track_(inst, 'panel_instantiated', { panel_id: id });
    return panel;
  }

  function panelOf(inst, sel) {
    if (!sel || !sel.dataset.panel) return null;
    return inst.root.querySelector('[data-shop-panel="' + CSS.escape(sel.dataset.panel) + '"]');
  }

  function select(inst, sel, opts) {
    opts = opts || {};
    var id = sel.dataset.panel;
    if (!id) return;

    /* Re-selecting the active selector is a no-op: no scroll jump, no duplicate event. */
    if (inst.active === sel && !opts.force) return;

    hydrate(inst, id);

    /* 2026-09-26: a swap that hides the control holding focus must not drop
       focus to <body> (measured live: the focused "הוספה לסל" vanished with
       its panel). Focus moves to the chosen tile instead, without a page
       scroll. */
    var outgoing = panelOf(inst, inst.active);
    var focusLeaves = !!(outgoing && outgoing !== panelOf(inst, sel) && outgoing.contains(document.activeElement));

    inst.selectors.forEach(function (s) {
      var on = s === sel;
      s.classList.toggle('is-on', on);
      if (on) s.setAttribute('aria-current', 'true'); else s.removeAttribute('aria-current');
      var p = panelOf(inst, s);
      if (p) p.hidden = !on;
    });

    if (focusLeaves) {
      try { sel.focus({ preventScroll: true }); } catch (e) { sel.focus(); }
    }

    inst.active = sel;

    /* Rail-snap (2026-08-22): a tap seats the chosen tile at the snap START
       (its on-grid position) rather than scrollIntoView(inline:center). With
       mandatory snapping a centred tile would be re-snapped to a neighbour's
       start, and settle() would then select THAT neighbour — the selected
       tile and the settled tile must be the same tile. No page scroll: setPos
       only writes the track's scrollLeft. */
    if (opts.scrollIntoView !== false) {
      seatAt(inst, inst.selectors.indexOf(sel), false);
    }
    syncClones(inst);

    syncDots(inst);

    var live = inst.root.querySelector('[data-shop-live]');
    if (live) live.textContent = (sel.dataset.announce || sel.textContent || '').trim();

    if (!opts.silent) {
      track_(inst, inst.mode === 'vendor' ? 'vendor_selected' : 'category_selected',
        { panel_id: id, label: (sel.dataset.label || '').trim(), handle: sel.dataset.handle || '', source: opts.source || 'tap' });
    }
  }

  /* ---------------------------------------------------------------- dots */
  /* R4 (2026-08-04, owner-reported) — the dots never moved.
     They were written ONLY by select(), so they reported SELECTION while
     visually reading, beneath a horizontally scrollable selector track, as a
     scroll position. Measured on preview 150882386073 at 390x844: a 150px CDP
     swipe moved .yshop-track scrollLeft 0 -> -656 of a -786 range and all four
     dot groups on the homepage stayed on index 0.

     They now mean one thing — which real selector is nearest the track's
     inline centre — and have one writer. Selection still lights the expected
     dot because select() centres the chosen selector, so both paths converge
     here instead of writing the same class from two places.

     Clones are excluded deliberately: [data-shop-clone] are inert edge copies
     built by buildLoop() and counting them would land the indicator on a
     duplicate. Geometry is read from the live rects rather than an index, so
     RTL needs no special case — there is no left/right here, only distance. */
  function syncDots(inst) {
    var dots = inst.root.querySelector('[data-shop-dots]');
    if (!dots || !inst.track || !inst.selectors.length) return;

    /* R4 (2026-08-04, adversarial finding #5) — a pagination indicator on a
       track that cannot page is furniture, and worse, it lights an arbitrary
       dot: with "nearest selector to the track centre" as the rule, a track
       with no overflow always has some winner, and it can never change because
       there is nothing to scroll. Measured on the vendors-3 instance at 390:
       2 selectors, 2 dots, scrollWidth - clientWidth = 0, indicator frozen on
       index 1 in both swipe directions.
       Hidden rather than left frozen. The dots are aria-hidden decoration, so
       nothing is lost; the selectors themselves remain the real control. */
    var canScroll = (inst.track.scrollWidth - inst.track.clientWidth) > 1;
    dots.hidden = !canScroll;
    if (!canScroll) return;

    /* Rail-snap (2026-08-22): the dot is the SETTLED tile — the same
       settledIndex() that drives selection — not an independent
       nearest-to-centre search. Two computations were how the dot and the
       product panel came to disagree. */
    var best = settledIndex(inst);
    if (best < 0) return;
    for (var j = 0; j < dots.children.length; j++) {
      dots.children[j].classList.toggle('is-on', j === best);
    }
  }

  /* ------------------------------------------------------------------ init */

  function init(root) {
    if (!root || instances.has(root)) return instances.get(root);   /* idempotent */

    var ac = new AbortController();
    var sig = { signal: ac.signal };
    var inst = {
      root: root,
      uid: root.dataset.shopUid || root.id || '',
      mode: root.dataset.shopMode || 'category',
      track: root.querySelector('[data-shop-track]'),
      panelHost: root.querySelector('[data-shop-panels]'),
      selectors: Array.prototype.slice.call(root.querySelectorAll('[data-shop-sel]')),
      arrows: Array.prototype.slice.call(root.querySelectorAll('[data-shop-arrow]')),
      hydrated: new Set(),
      fired: new Set(),
      active: null,
      moved: false,       /* a person moved the band since focus last entered it (settle()) */
      ac: ac,
    };
    instances.set(root, inst);
    root.setAttribute('data-shop-ready', '');

    /* A click on an inert edge clone activates its REAL counterpart, so aria-current,
       the dots and analytics all stay single-sourced. */
    root.addEventListener('click', function (e) {
      var c = e.target.closest && e.target.closest('[data-shop-clone]');
      if (!c) return;
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      /* already the active vendor -> the click means "go there" (see selectors) */
      if (inst.active && inst.active.dataset.panel === c.dataset.panel && c.getAttribute('href')) return;
      e.preventDefault();
      for (var i = 0; i < inst.selectors.length; i++) {
        if (inst.selectors[i].dataset.panel === c.dataset.panel) { select(inst, inst.selectors[i], { scrollIntoView: false }); break; }
      }
    }, sig);

    /* Selector activation. Modified clicks and middle clicks fall through to the href. */
    inst.selectors.forEach(function (sel) {
      if (sel.getAttribute('aria-current') === 'true') inst.active = sel;
      sel.addEventListener('click', function (e) {
        if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        /* 2026-09-02 (team QA: "clicking a vendor tile doesn't open the vendor
           page"). First click on a tile selects it and reveals its rail; a
           click on the tile that is ALREADY active has nothing left to select,
           so it follows the tile's real href to /pages/vendor/<handle>. The
           selector stays a real link either way. */
        if (inst.active === sel && sel.getAttribute('href')) return;
        e.preventDefault();
        select(inst, sel);
      }, sig);
    });

    /* Selector arrows — exactly pageSize() whole cards. */
    inst.arrows.forEach(function (btn) {
      btn.addEventListener('click', function () {
        inst.moved = true;   /* mouse, Enter or Space: the settled tile is chosen */
        page(inst.track, btn.dataset.dir, inst);
        track_(inst, 'selector_advanced', { direction: btn.dataset.dir });
      }, sig);
    });

    /* Product-rail arrows are NOT handled here any more. This file owns the
       selector BAND (overlaid arrows, infinite loop, panel activation); the
       product rails inside the panels are the shared merchandising rail and are
       owned by assets/yojo-rail-controls.js. The old handler here bound clicks
       but never tested overflow, so every rail rendered two live-looking arrows
       — including rails whose cards all fitted. One owner, one behaviour.

       Analytics for rail paging still belongs to this instance, so listen for
       the shared controller's clicks rather than re-implementing them. */
    root.addEventListener('click', function (e) {
      var b = e.target.closest && e.target.closest('[data-rail-arrow]');
      if (!b || b.disabled) return;
      var g = b.closest('[data-rail-group]');
      track_(inst, 'rail_advanced', { rail: g ? g.dataset.railId : '', direction: b.dataset.dir });
    }, sig);

    if (inst.track) {
      /* Who moved the band (2026-09-26, see settle()). A finger, pen, mouse
         press or wheel on the track is a person; focus arriving in the track
         or the active panel (Tab, a screen-reader cursor) is not, and cancels
         any earlier gesture so the focus-scroll that follows chooses nothing. */
      var byHand = function () { inst.moved = true; };
      inst.track.addEventListener('pointerdown', byHand, sig);
      inst.track.addEventListener('touchstart', byHand, { passive: true, signal: ac.signal });
      inst.track.addEventListener('wheel', byHand, { passive: true, signal: ac.signal });
      root.addEventListener('focusin', function (e) {
        var p = panelOf(inst, inst.active);
        if (inst.track.contains(e.target) || (p && p.contains(e.target))) inst.moved = false;
      }, sig);

      buildLoop(inst);
      /* Seat the server-rendered active tile at the snap start so the first
         settle() finds the selection already in place (no boot-time swap). */
      if (inst.active) seatAt(inst, inst.selectors.indexOf(inst.active), true);
      var settleT;
      var dotsRaf = 0;
      inst.track.addEventListener('scroll', function () {
        syncArrows(inst);
        /* The indicator follows the finger, so it updates every frame rather
           than waiting for the 140ms settle below — a dot that only moved once
           scrolling stopped would read as broken for the whole gesture. rAF
           coalesced: scroll fires far more often than the screen repaints. */
        if (!dotsRaf) {
          dotsRaf = requestAnimationFrame(function () { dotsRaf = 0; syncDots(inst); });
        }
        /* Once the band has come to rest: re-seat out of the clone zone, then
           make the settled tile the selection (settle()). 140ms debounce is
           the fallback for engines without scrollend (Safari < 26). */
        clearTimeout(settleT);
        settleT = setTimeout(function () { settle(inst); }, 140);
      }, { passive: true, signal: ac.signal });
      if ('onscrollend' in window) {
        inst.track.addEventListener('scrollend', function () { clearTimeout(settleT); settle(inst); }, { signal: ac.signal });
      }
      /* Finger down/up, for normalise() and settle() (both skip while held).
         Touch events only: a pointercancel fires the moment the browser takes
         the pan, while the finger is still down, so pointer events would clear
         the flag early. A mouse press does not drag-scroll this track and
         never sets it. Release does NOT settle directly: the browser's own
         snap/fling is still moving the band at touchend, and re-seating then
         is the same mid-motion jump. It re-arms the debounce instead, so the
         settle runs once the motion has stopped (each scroll event re-arms it
         again), including when release lands on a tile edge and nothing moves
         afterwards. */
      inst.track.addEventListener('touchstart', function () { inst.touching = true; }, { passive: true, signal: ac.signal });
      var release = function (e) {
        if (e.touches && e.touches.length) return;   /* another finger is still down */
        inst.touching = false;
        clearTimeout(settleT);
        settleT = setTimeout(function () { settle(inst); }, 140);
      };
      inst.track.addEventListener('touchend', release, { passive: true, signal: ac.signal });
      inst.track.addEventListener('touchcancel', release, { passive: true, signal: ac.signal });
      syncArrows(inst);
      syncClones(inst);
      syncDots(inst);
      /* Re-evaluate on resize — the arrows must disappear when 3 cards start fitting. */
      if ('ResizeObserver' in window) {
        /* Observe the section ROOT, never the track: cloning changes the track's
           scrollWidth, which would re-fire the observer and rebuild forever. */
        var lastW = inst.root.clientWidth, roT;
        inst.ro = new ResizeObserver(function () {
          clearTimeout(roT);
          roT = setTimeout(function () {
            var w = inst.root.clientWidth;
            if (w === lastW) { syncArrows(inst); syncDots(inst); return; }   /* not a real viewport change */
            lastW = w;
            if (inst.loop && inst.loop.pv !== perView(inst.track)) {
              inst.root.querySelectorAll('[data-shop-clone]').forEach(function (c) { c.remove(); });
              inst.loop = null;
              buildLoop(inst);
              /* buildLoop() seats the FIRST real tile; without re-seating the
                 selection, the next settle() switched vendor on a mere window
                 resize (2026-09-26). Same seat as init. */
              if (inst.active) seatAt(inst, inst.selectors.indexOf(inst.active), true);
              syncClones(inst);
            }
            syncArrows(inst);
            syncDots(inst);   /* card pitch changed, so the centred selector may have */
          }, 180);
        });
        inst.ro.observe(inst.root);
      }
    }

    /* ------------------------------------------------ NO AUTO-ADVANCE
       (2026-09-26, audit website-az-2026-09-25 runner home-a11y-speed #2,
       owner-approved). The band moves only when a person moves it.

       History. The 2026-08-12 owner ruling (mobile motion correction) added a
       3000ms desktop tick that advanced the TRACK "never the active panel, so
       no content swaps under the reader". Rail-snap (2026-08-22) then made
       the settled tile the selection (settle()), so from that day every tick
       DID swap the products and write the aria-live region: the ruling's own
       no-swap condition could no longer be met, because a tick cannot move
       the band and also keep tile and panel in agreement. Measured live at
       1440: one band changed vendor 4 times in 13s with the mouse resting on
       the page, announcing each change; in a keyboard-only run 3 of 4 bands
       switched with no activation and one hid the focused "הוספה לסל" (focus
       fell to <body>). Hover never stopped it. WCAG 2.2.2 Pause, Stop, Hide
       (A), 3.2.1 On Focus (A), 2.4.3 Focus Order (A), 4.1.3 noise.

       Removed rather than patched: every remaining selection path (tap,
       swipe, wheel, arrow) is a person's choice. Phones stopped auto-advancing
       (rail-snap), and product rails (yojo-rail-controls.js) never did. Do
       not reintroduce a timer here without an owner ruling that accepts the
       products changing under a reader. */

    /* One impression event per instance when it first enters the viewport. */
    if ('IntersectionObserver' in window) {
      inst.io = new IntersectionObserver(function (entries) {
        entries.forEach(function (en) {
          if (!en.isIntersecting) return;
          track_(inst, inst.mode === 'vendor' ? 'vendor_group_viewed' : 'category_scroller_viewed', { once: true, id: inst.uid });
          inst.io.disconnect();
        });
      }, { threshold: 0.4 });
      inst.io.observe(root);
    }

    return inst;
  }

  function destroy(root) {
    var inst = instances.get(root);
    if (!inst) return;
    inst.ac.abort();                       /* removes every listener in one call */
    if (inst.ro) inst.ro.disconnect();
    root.querySelectorAll('[data-shop-clone]').forEach(function (c) { c.remove(); });
    if (inst.io) inst.io.disconnect();
    instances.delete(root);
    root.removeAttribute('data-shop-ready');
  }

  function initAll(scope) {
    (scope || document).querySelectorAll('[data-yojo-shop]').forEach(init);
  }

  /* ------------------------------------------------------------- lifecycle */

  if (document.readyState !== 'loading') initAll();
  else document.addEventListener('DOMContentLoaded', function () { initAll(); });

  document.addEventListener('shopify:section:load', function (e) {
    var root = e.target.querySelector ? e.target.querySelector('[data-yojo-shop]') : null;
    if (root) { destroy(root); init(root); }         /* destroy first → never double-bind */
  });

  document.addEventListener('shopify:section:unload', function (e) {
    var root = e.target.querySelector ? e.target.querySelector('[data-yojo-shop]') : null;
    if (root) destroy(root);
  });

  /* Theme editor: selecting a block opens its panel so the merchandiser can see it. */
  document.addEventListener('shopify:block:select', function (e) {
    var sel = e.target.matches && e.target.matches('[data-shop-sel]')
      ? e.target : (e.target.querySelector ? e.target.querySelector('[data-shop-sel]') : null);
    if (!sel) return;
    var root = sel.closest('[data-yojo-shop]');
    if (root) select(init(root), sel, { silent: true });
  });

  window.YojoShop = { init: init, initAll: initAll, destroy: destroy };
})();
